import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Sparkles } from 'lucide-react';

const ExclusiveMemberships = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const membershipTiers = [
    {
      name: 'RESET & GLOW',
      price: '$99',
      period: 'Month',
      benefits: [
        '1 per month: 30-minute Signature Hydrafacial',
        '5% Savings on Each Additional Service Booked',
        '10% Savings on Laser, Waxing, and Microneedling Services',
        '10% Savings on 1 Amal Pilates Class',
        '$1 Towards Per Unit of Botox',
        'Vitamin Shot'
      ],
      color: 'from-olive-darkest/90 to-olive-darkest'
    },
    {
      name: 'RENEW & TIGHTEN',
      price: '$199',
      period: 'Month',
      benefits: [
        '1 per month: 60-minute Swedish or Deep Tissue Massage',
        '& 60-minute Deluxe Hydrafacial',
        '10% Savings on Each Additional Service Booked',
        '15% Savings on Laser, Waxing, and Microneedling Services',
        '20% Savings on 1 Amal Pilates Class',
        '$2 Towards Per Unit of Botox'
      ],
      color: 'from-gold/90 to-gold',
      featured: true
    },
    {
      name: 'LONGEVITY ELITE',
      price: '$299',
      period: 'Month',
      benefits: [
        '1 per month: 60-minute Swedish or Deep Tissue Massage w/Cupping,',
        '& 60-minute Deluxe Hydrafacial',
        '15% Savings on Each Additional Service Booked',
        '20% Savings on Laser, Waxing, and Microneedling Services',
        '10% Savings on Dermal Fillers',
        '10% Savings on 1 Amal Pilates Membership & 1 Free Pilates Class/month',
        '$3 Towards Per Unit of Botox'
      ],
      color: 'from-olive-darkest/95 to-olive-darkest/80'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Exclusive Memberships - Reset & Glow, Renew & Tighten | Century Aesthetics</title>
        <meta name="description" content="Join our exclusive spa memberships starting at $99/month. Get monthly HydraFacials, massages, Botox discounts, and more. Choose from Reset & Glow, Renew & Tighten, or Longevity Elite tiers." />
        <meta name="keywords" content="spa membership, monthly hydrafacial, wellness membership, beauty subscription, botox discount, massage membership, aesthetic membership" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/exclusive-memberships" />
        
        <meta property="og:title" content="Exclusive Memberships | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Transform your wellness journey with our exclusive membership tiers. Monthly treatments, savings, and special perks starting at $99/month." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/exclusive-memberships" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-nude/20 pt-28">
      
      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden" aria-label="Exclusive Memberships Hero">
        <div className="absolute inset-0 bg-gradient-to-br from-olive-darkest/5 to-gold/5"></div>
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Sparkles className="w-8 h-8 text-gold" aria-hidden="true" />
              <h1 className="text-5xl md:text-6xl font-heading font-bold text-olive-darkest">
                NEW YEAR <span className="italic font-light">New Glow</span>
              </h1>
              <Sparkles className="w-8 h-8 text-gold" aria-hidden="true" />
            </div>
            <p className="text-2xl text-olive-darkest/80 font-light tracking-wide mb-4">
              EXCLUSIVE MEMBERSHIP TIERS
            </p>
            <div className="w-32 h-1 bg-gold mx-auto"></div>
          </motion.div>
        </div>
      </section>

      {/* Membership Cards */}
      <section className="py-12 pb-20" aria-label="Membership Plans">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {membershipTiers.map((tier, index) => (
              <motion.article
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className={`relative ${tier.featured ? 'md:-mt-4 md:mb-4' : ''}`}
              >
                {tier.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gold text-olive-darkest px-6 py-2 rounded-full text-sm font-semibold tracking-wider shadow-lg">
                    MOST POPULAR
                  </div>
                )}
                <div className={`bg-gradient-to-br ${tier.color} rounded-2xl p-8 shadow-2xl h-full flex flex-col border-2 ${tier.featured ? 'border-gold' : 'border-gold/20'}`}>
                  {/* Header */}
                  <header className="text-center mb-8">
                    <h2 className="text-2xl font-heading font-bold text-white tracking-wider mb-4">
                      {tier.name}
                    </h2>
                    <div className="flex items-baseline justify-center gap-2 mb-2">
                      <span className="text-5xl font-heading font-light text-cream">
                        {tier.price}
                      </span>
                      <span className="text-lg text-cream/90">/ {tier.period}</span>
                    </div>
                    <div className="w-16 h-0.5 bg-cream/30 mx-auto"></div>
                  </header>

                  {/* Benefits */}
                  <ul className="space-y-4 flex-1">
                    {tier.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-cream flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="text-cream/95 text-sm leading-relaxed">
                          {benefit}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <motion.button
                    whileHover={{ y: -2, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={scrollToContact}
                    className="mt-8 w-full bg-cream text-olive-darkest px-6 py-4 rounded-lg font-semibold text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg"
                    aria-label={`Join ${tier.name} membership`}
                  >
                    Join Now
                  </motion.button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-16 bg-olive-darkest" aria-label="Transform Your Wellness Journey">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Transform Your Wellness Journey
            </h2>
            <p className="text-lg text-cream/90 max-w-3xl mx-auto leading-relaxed mb-8">
              Our exclusive membership tiers offer exceptional value with monthly treatments, significant savings on additional services, and special perks designed to support your beauty and wellness goals. Choose the tier that fits your lifestyle and start your transformation today.
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="bg-gold text-olive-darkest px-10 py-4 rounded-lg font-semibold text-lg tracking-wide hover:bg-cream transition-all duration-300 shadow-xl"
              aria-label="Schedule consultation for membership"
            >
              Schedule Consultation
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Spacer */}
      <div className="h-16 bg-cream"></div>
    </div>
    </>
  );
};

export default ExclusiveMemberships;